import React, { createContext, useContext, useState, ReactNode } from 'react';
import { 
  StrategicPillar, 
  KeyPerformanceArea, 
  KPI, 
  StrategicInitiative,
  FunctionalFocusArea 
} from '@/types/kpi';
import { Objective } from '@/types/objectives';
import { 
  strategicPillars, 
  keyPerformanceAreas, 
  kpis, 
  strategicInitiatives,
  functionalFocusAreas 
} from '@/data/strategicData';

interface PerformanceContextType {
  // Core data
  pillars: StrategicPillar[];
  kpas: KeyPerformanceArea[];
  kpis: KPI[];
  strategicInitiatives: StrategicInitiative[];
  functionalAreas: FunctionalFocusArea[];
  objectives: Objective[];
  
  // Cross-reference functions
  getObjectivesByKPI: (kpiId: string) => Objective[];
  getKPIsByObjective: (objectiveId: string) => KPI[];
  getInitiativesByObjective: (objectiveId: string) => StrategicInitiative[];
  getObjectivesByInitiative: (initiativeId: string) => Objective[];
  getKPAsByPillar: (pillarId: string) => KeyPerformanceArea[];
  getKPIsByKPA: (kpaId: string) => KPI[];
  getInitiativesByPillar: (pillarId: string) => StrategicInitiative[];
  
  // Progress calculations
  calculateKPIProgress: (kpiId: string) => number;
  calculateInitiativeProgress: (initiativeId: string) => number;
  calculatePillarProgress: (pillarId: string) => number;
  
  // Data management
  updateObjective: (objective: Objective) => void;
  addObjective: (objective: Objective) => void;
  deleteObjective: (objectiveId: string) => void;
  updateKPI: (kpi: KPI) => void;
}

const PerformanceContext = createContext<PerformanceContextType | undefined>(undefined);

interface PerformanceProviderProps {
  children: ReactNode;
}

export const PerformanceProvider: React.FC<PerformanceProviderProps> = ({ children }) => {
  const [objectives, setObjectives] = useState<Objective[]>([]);

  // Objectives are sourced from the performance API in the live views; this
  // context no longer seeds mock/sample objectives (removed — was demo data).

  // Cross-reference functions
  const getObjectivesByKPI = (kpiId: string): Objective[] => {
    return objectives.filter(obj => obj.kpiIds?.includes(kpiId) || false);
  };

  const getKPIsByObjective = (objectiveId: string): KPI[] => {
    const objective = objectives.find(obj => obj.id === objectiveId);
    if (!objective?.kpiIds) return [];
    return kpis.filter(kpi => objective.kpiIds!.includes(kpi.id));
  };

  const getInitiativesByObjective = (objectiveId: string): StrategicInitiative[] => {
    const objective = objectives.find(obj => obj.id === objectiveId);
    if (!objective?.strategicInitiativeId) return [];
    return strategicInitiatives.filter(init => init.id === objective.strategicInitiativeId);
  };

  const getObjectivesByInitiative = (initiativeId: string): Objective[] => {
    return objectives.filter(obj => obj.strategicInitiativeId === initiativeId);
  };

  const getKPAsByPillar = (pillarId: string): KeyPerformanceArea[] => {
    return keyPerformanceAreas.filter(kpa => kpa.pillarId === pillarId);
  };

  const getKPIsByKPA = (kpaId: string): KPI[] => {
    return kpis.filter(kpi => kpi.kpaId === kpaId);
  };

  const getInitiativesByPillar = (pillarId: string): StrategicInitiative[] => {
    return strategicInitiatives.filter(init => init.pillarId === pillarId);
  };

  // Progress calculations
  const calculateKPIProgress = (kpiId: string): number => {
    const kpi = kpis.find(k => k.id === kpiId);
    if (!kpi?.currentValue || !kpi?.targetValue) return 0;
    return Math.min((kpi.currentValue / kpi.targetValue) * 100, 100);
  };

  const calculateInitiativeProgress = (initiativeId: string): number => {
    const initiative = strategicInitiatives.find(init => init.id === initiativeId);
    if (!initiative) return 0;
    
    // Calculate based on linked KPIs and objectives
    const linkedKPIs = kpis.filter(kpi => initiative.kpiIds.includes(kpi.id));
    const linkedObjectives = getObjectivesByInitiative(initiativeId);
    
    let totalProgress = 0;
    let count = 0;
    
    // Include KPI progress
    linkedKPIs.forEach(kpi => {
      totalProgress += calculateKPIProgress(kpi.id);
      count++;
    });
    
    // Include objective progress
    linkedObjectives.forEach(obj => {
      if (obj.targetValue && obj.currentValue) {
        totalProgress += Math.min((obj.currentValue / obj.targetValue) * 100, 100);
        count++;
      }
    });
    
    return count > 0 ? totalProgress / count : initiative.progress;
  };

  const calculatePillarProgress = (pillarId: string): number => {
    const pillarInitiatives = getInitiativesByPillar(pillarId);
    if (pillarInitiatives.length === 0) return 0;
    
    const totalProgress = pillarInitiatives.reduce((sum, init) => 
      sum + calculateInitiativeProgress(init.id), 0
    );
    
    return totalProgress / pillarInitiatives.length;
  };

  // Data management functions
  const updateObjective = (updatedObjective: Objective) => {
    setObjectives(prev => 
      prev.map(obj => obj.id === updatedObjective.id ? updatedObjective : obj)
    );
  };

  const addObjective = (newObjective: Objective) => {
    setObjectives(prev => [...prev, newObjective]);
  };

  const deleteObjective = (objectiveId: string) => {
    setObjectives(prev => prev.filter(obj => obj.id !== objectiveId));
  };

  const updateKPI = (updatedKPI: KPI) => {
    // In a real implementation, this would update the KPI in the backend
    // For now, we'll just trigger a re-render
    if (import.meta.env.DEV) console.log('KPI updated:', updatedKPI);
  };

  const contextValue: PerformanceContextType = {
    // Core data
    pillars: strategicPillars,
    kpas: keyPerformanceAreas,
    kpis,
    strategicInitiatives,
    functionalAreas: functionalFocusAreas,
    objectives,
    
    // Cross-reference functions
    getObjectivesByKPI,
    getKPIsByObjective,
    getInitiativesByObjective,
    getObjectivesByInitiative,
    getKPAsByPillar,
    getKPIsByKPA,
    getInitiativesByPillar,
    
    // Progress calculations
    calculateKPIProgress,
    calculateInitiativeProgress,
    calculatePillarProgress,
    
    // Data management
    updateObjective,
    addObjective,
    deleteObjective,
    updateKPI,
  };

  return (
    <PerformanceContext.Provider value={contextValue}>
      {children}
    </PerformanceContext.Provider>
  );
};

export const usePerformance = (): PerformanceContextType => {
  const context = useContext(PerformanceContext);
  if (context === undefined) {
    throw new Error('usePerformance must be used within a PerformanceProvider');
  }
  return context;
};
